import { FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Constants } from '../../shared/models/common/constants';

export class Utils {
  static SILENT = { onlySelf: true, emitEvent: false };
  static UPDATE_MODEL_ONLY = { onlySelf: true, emitEvent: false, emitModelToViewChange: false };
  static UPDATE_VIEW_ONLY = { onlySelf: true, emitEvent: false, emitViewToModelChange: false };

  public static toFixed(value: number, decimals?: number): string | undefined {
    if (value === undefined) {
      return undefined;
    }
    const temp = Utils._toFixed(value, decimals);
    return Utils._toFixed(Number(temp), decimals);
  }

  private static _toFixed(value: number, decimals?: number): string | undefined {
    if (value === undefined) {
      return undefined;
    }
    if (decimals === undefined) {
      decimals = Number.isInteger(value) ? 0 : Constants.DEFAULT_DECIMALS;
    } else {
      decimals = Number.isInteger(value) ? 0 : decimals;
    }
    return Number.parseFloat(value.toString()).toFixed(decimals);
  }

  public static sort(sortings: any[], sorting, descend?: boolean) {
    sortings.sort((el1, el2) => {
      let r: number;
      if (el1[sorting] > el2[sorting]) {
        r = 1;
      } else if (el1[sorting] === el2[sorting]) {
        r = 0;
      } else {
        r = -1;
      }
      if (descend) {
        r = -r;
      }
      return r;
    });
    return sortings;
  }

  public static sortWithReturn(sortings: any[], sorting, descend?: boolean) {
    sortings.sort((el1, el2) => {
      let r: number;
      if (el1[sorting] > el2[sorting]) {
        r = 1;
      } else if (el1[sorting] === el2[sorting]) {
        r = 0;
      } else {
        r = -1;
      }
      if (descend) {
        r = -r;
      }
      return r;
    });
    return sortings;
  }

  public static sortElasticSearchFilterList(array: Array<any>, args: string): Array<any> {
    if (array !== undefined) {
      return array.sort((a: any, b: any) => {
        const aValue = a._source[args];
        const bValue = b._source[args];

        if (aValue < bValue) {
          return -1;
        } else if (aValue > bValue) {
          return 1;
        } else {
          return 0;
        }
      });
    }
    return array;
  }

  public static sortFormArray(array: Array<any>, args: string): Array<any> {
    if (array !== undefined) {
      return array.sort((a: any, b: any) => {
        const aValue = a.controls[args].value;
        const bValue = b.controls[args].value;

        if (aValue < bValue) {
          return -1;
        } else if (aValue > bValue) {
          return 1;
        } else {
          return 0;
        }
      });
    }
    return array;
  }

  public static isArray(element) {
    return Array.isArray(element);
  }

  public static isEmptyArray(element) {
    return element.length && element.length === 0;
  }

  public static isObject(element) {
    return element !== null && typeof element === 'object' && !Array.isArray(element);
  }
  public static isEmptyObject(obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  public static isEmpty(value: number | string): boolean {
    return value === undefined || value.toString().trim() === '' || (Utils.isArray(value) && Utils.isEmptyArray(value)) || (Utils.isObject(value) && Utils.isEmptyObject(value));
  }

  /*
   * params
   * stack: haystack
   * form.field name of object
   * hay: value of the form field
   */
  public static findItem(stack: any[], member: string, hay: string | number) {
    let found;
    for (const item of stack) {
      if (item[member] === hay) {
        found = item;
        break;
      }
    }
    return found;
  }

  public static findItemIgnoreCase(stack: any[], member: string, hay: string) {
    let found;
    for (const item of stack) {
      if (item[member].toUpperCase() === hay.toUpperCase()) {
        found = item;
        break;
      }
    }
    return found;
  }

  public static matchStr(a: string, b: string) {
    if (!a || !b) {
      return false;
    }
    return a.toUpperCase() === b.toUpperCase();
  }

  public static isEqual(value, other) {
    const type = Object.prototype.toString.call(value);
    if (type !== Object.prototype.toString.call(other)) {
      return false;
    }

    // If items are not an object or array, return false
    if (['[object Array]', '[object Object]'].indexOf(type) < 0) {
      return false;
    }

    // Compare the length of the length of the two items
    const valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
    const otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
    if (valueLen !== otherLen) {
      return false;
    }

    // Compare two items
    // tslint:disable-next-line: only-arrow-functions
    const compare = function (item1, item2) {
      const itemType = Object.prototype.toString.call(item1);
      if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
        if (!Utils.isEqual(item1, item2)) {
          return false;
        }
      } else {
        if (itemType !== Object.prototype.toString.call(item2)) {
          return false;
        }
        // Else if it's a function, convert to a string and compare
        // Otherwise, just compare
        if (itemType === '[object Function]') {
          if (item1.toString() !== item2.toString()) {
            return false;
          }
        } else {
          if (item1 !== item2) {
            return false;
          }
        }
      }
    };
    // Compare properties
    if (type === '[object Array]') {
      for (let i = 0; i < valueLen; i++) {
        if (compare(value[i], other[i]) === false) {
          return false;
        }
      }
    } else {
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          if (compare(value[key], other[key]) === false) {
            return false;
          }
        }
      }
    }
    // If nothing failed, return true
    return true;
  }

  static isDateWithinTwentyYears(dateStr: string): boolean {
    if (!dateStr) {
      return undefined;
    }
    const date = moment(Utils.utcDateOf(dateStr));
    const startMoment = moment({ year: moment().year() - 20, month: 0, day: 1 });
    const endMoment = moment({ year: moment().year() + 20, month: 11, day: 31 });
    return date.isBetween(startMoment, endMoment, undefined, '[]');
  }

  static getCurrentYearYY() {
    return new Date().getUTCFullYear() % 100;
  }

  static getCurrentYear() {
    return new Date().getUTCFullYear();
  }

  // Get range array based on inputs
  static range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

  /**
   * format tokenized text
   * e.g text = 'I am {0} {1}'
   * params = ['John', 'Smith']
   * output 'I am John Smith'
   */
  static format(text: string, params: any[]): string {
    if (params && params.length > 0) {
      params.forEach((param, index) => {
        text = text.replace(new RegExp('\\{' + index + '\\}', 'g'), param);
      });
      return text;
    }
    return text;
  }

  /**
   * this is to keep observable alive after error posted
   */
  static errorsomeStream(stream: Observable<any>) {
    return stream.pipe(catchError((e) => e));
  }

  static uuid() {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      // tslint:disable-next-line: no-bitwise
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      // tslint:disable-next-line: no-bitwise
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
  }

  static utcNow() {
    const now = new Date();
    return Utils.utcDate(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  }

  static utcDate(year, month, date) {
    return new Date(Date.UTC(year, month, date));
  }

  static utcDateOf(date: any) {
    if (!date) {
      return undefined;
    }
    if (typeof date.getMonth === 'function') {
      return Utils.utcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    }
    if (typeof date === 'string') {
      const d = new Date(date);
      return Utils.utcDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    }
    // return moment(date).utc(true);
  }

  static momentByUtcDate(year, month, date) {
    return moment(Utils.utcDate(year, month, date));
  }

}

